<template>
    <div v-if="resource && shouldShow" class="my-6 rounded bg-neutral-100 dark:bg-neutral-800">
        <div class="border-b border-neutral-200 p-3 dark:border-neutral-700">
            <div class="flex items-start justify-between gap-3">
                <div>
                    <div class="font-semibold">Marketplace Listing</div>
                    <div class="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        {{ statusLabel }}
                    </div>
                    <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                        <span class="inline-flex items-center gap-1"
                              :title="statLabel(viewsCount, 'view')"
                        >
                            <Icon icon="eye" class="text-[0.68rem]"/>
                            {{ viewsCount }}
                        </span>
                        <span class="inline-flex items-center gap-1"
                              :title="statLabel(importsCount, 'download')"
                        >
                            <Icon icon="download" class="text-[0.68rem]"/>
                            {{ importsCount }}
                        </span>
                        <span class="inline-flex items-center gap-1"
                              :title="ratingLabel"
                        >
                            <Icon icon="star" class="text-[0.68rem]"/>
                            {{ averageRating || 'New' }}
                        </span>
                    </div>
                </div>
                <div class="rounded border px-2 py-0.5 text-xs font-semibold"
                     :class="published ? 'border-brand-primary-400 text-brand-primary-500' : 'border-neutral-400 text-neutral-500'"
                >
                    {{ published ? 'Published' : 'Not Listed' }}
                </div>
            </div>
        </div>

        <div class="space-y-3 p-3">
            <div class="text-sm text-neutral-600 dark:text-neutral-300">
                {{ helpText }}
            </div>

            <div v-if="!resource.marketplacePublishable && resource.marketplacePublishBlockedReason"
                 class="rounded border border-neutral-300 p-2 text-xs text-neutral-600 dark:border-neutral-700 dark:text-neutral-300"
            >
                {{ resource.marketplacePublishBlockedReason }}
            </div>

            <div class="flex flex-wrap items-center gap-2">
                <Button v-if="resource.marketplacePublishable"
                        :loading="publishing"
                        primary
                        size="sm"
                        @click="handlePublish"
                >
                    <Icon icon="arrow-up-from-bracket" class="mr-2"/>
                    {{ published ? 'Update Listing' : 'Publish' }}
                </Button>

                <Button v-if="published"
                        :loading="unpublishing"
                        danger
                        outlined
                        size="sm"
                        @click="handleUnpublish"
                >
                    Unpublish
                </Button>
            </div>
        </div>
    </div>
</template>

<script>
import {
  MarketplaceItemService,
} from '@/internal';

const marketplace = new MarketplaceItemService();

export default {
  name: 'MarketplaceListingPanel',
  computed: {
    helpText() {
      if (this.resourceType === 'template') {
        return 'Publishing creates a marketplace snapshot of this template and its custom movements.';
      }

      return 'Publishing creates a marketplace snapshot of this movement.';
    },
    published() {
      return !!(this.listing && this.listing.publishedAt && !this.listing.hiddenAt);
    },
    averageRating() {
      return this.listing?.averageRating || null;
    },
    importsCount() {
      return this.listing?.importsCount || 0;
    },
    ratingLabel() {
      return this.averageRating ? `${this.averageRating} rating` : 'New rating';
    },
    shouldShow() {
      return !!(this.resource?.marketplacePublishable || this.listing);
    },
    statusLabel() {
      return this.published ? 'Available for other users to import.' : 'Only you can see this resource.';
    },
    viewsCount() {
      return this.listing?.viewsCount || 0;
    },
  },
  data() {
    return {
      listing: null,
      publishing: false,
      unpublishing: false,
    };
  },
  methods: {
    handlePublish() {
      const wasPublished = this.published;

      this.publishing = true;

      const action = this.resourceType === 'template'
        ? marketplace.publishTemplate(this.resource.id)
        : marketplace.publishMovement(this.resource.id);

      action.then((response) => {
        this.listing = this.itemFromResponse(response);
        this.$store.dispatch('ui/snackbar', {
          message: wasPublished ? 'Marketplace listing updated.' : 'Published to Marketplace.',
          type: 'success',
        });
      }).catch((error) => {
        this.$store.dispatch('ui/snackbar', {
          message: error.response?.data?.message || 'Unable to publish.',
          type: 'danger',
        });
      }).finally(() => {
        this.publishing = false;
      });
    },
    handleUnpublish() {
      if (!this.listing) {
        return;
      }

      this.unpublishing = true;

      marketplace.destroy(this.listing.uuid || this.listing.id).then((response) => {
        this.listing = this.itemFromResponse(response);
        this.$store.dispatch('ui/snackbar', {
          message: 'Marketplace listing unpublished.',
          type: 'success',
        });
      }).catch((error) => {
        this.$store.dispatch('ui/snackbar', {
          message: error.response?.data?.message || 'Unable to unpublish.',
          type: 'danger',
        });
      }).finally(() => {
        this.unpublishing = false;
      });
    },
    itemFromResponse(response) {
      const data = response?.data?.data;

      if (!data) {
        return null;
      }

      return {
        id: data.id,
        uuid: data.attributes?.uuid,
        publishedAt: data.attributes?.published_at,
        hiddenAt: data.attributes?.hidden_at,
        averageRating: data.attributes?.average_rating,
        importsCount: data.attributes?.imports_count || 0,
        viewsCount: data.attributes?.views_count || 0,
      };
    },
    statLabel(value, singular) {
      const count = Number(value) || 0;

      return `${count} ${this.$pluralize(singular, count)}`;
    },
    syncListing() {
      this.listing = this.resource?.marketplaceItem || null;
    },
  },
  props: {
    resource: {
      default: null,
      required: false,
    },
    resourceType: {
      required: true,
      type: String,
    },
  },
  watch: {
    resource: {
      immediate: true,
      handler() {
        this.syncListing();
      },
    },
  },
}
</script>
